import React, { useState } from 'react';
import { Link, Mail, UserPlus, Trash2, RefreshCw, Send, Activity, User, Shield, AlertCircle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../ui/Card';
import { Button } from '../../ui/Button';
import { Input } from '../../ui/Input';
import { Badge } from '../../ui/Badge';
import { cn } from '../../../lib/utils';

const TeamSettings = ({ 
  teamInfo, 
  members, 
  activityLog, 
  activityHasMore, 
  onRemoveMember, 
  onUpdateTeam, 
  onRegenerateInvite, 
  onSendInvites, 
  loadMoreActivities, 
  loading 
}) => {
  const [teamName, setTeamName] = useState(teamInfo?.name || '');
  const [inviteEmails, setInviteEmails] = useState('');
  const [confirmModal, setConfirmModal] = useState(null);
  const [copySuccess, setCopySuccess] = useState('');
  const [inviteError, setInviteError] = useState('');
  const [inviteLoading, setInviteLoading] = useState(false);

  const handleUpdateName = () => {
    if (teamName.trim() && teamName !== teamInfo.name) {
      onUpdateTeam({ name: teamName });
    }
  };

  const handleCopyInvite = () => {
    if (teamInfo?.invite_link) {
      navigator.clipboard.writeText(teamInfo.invite_link);
      setCopySuccess('Copied!');
      setTimeout(() => setCopySuccess(''), 2000);
    }
  };

  const handleSendInvites = async () => {
    if (inviteEmails.trim()) {
      setInviteError('');
      setInviteLoading(true);

      try {
        const emails = inviteEmails.split(',').map(e => e.trim()).filter(e => e);
        await onSendInvites(teamInfo?.id, emails, teamInfo?.invite_link);
        setInviteEmails('');
      } catch (err) {
        // Extract error message from response (axios error structure)
        const errorMessage = err?.response?.data?.message
          || err?.response?.data?.detail
          || err?.message
          || 'Failed to send invitations. Please try again.';
        setInviteError(errorMessage);
      } finally {
        setInviteLoading(false);
      }
    }
  };

  const handleRemoveMember = (member) => {
    setConfirmModal({
      title: 'Remove Member',
      message: `Are you sure you want to remove ${member.fullname || member.email} from the team?`,
      onConfirm: () => {
        onRemoveMember(member.user_id);
        setConfirmModal(null);
      }
    });
  };

  const getInitials = (name) => {
    return name ? name.charAt(0).toUpperCase() : 'U';
  };

  const getActivityIcon = (type) => {
    switch (type) {
      case 'member_joined': return <UserPlus className="w-4 h-4 text-green-500" />;
      case 'member_removed': return <Trash2 className="w-4 h-4 text-red-500" />;
      case 'team_updated': return <RefreshCw className="w-4 h-4 text-blue-500" />;
      default: return <Activity className="w-4 h-4 text-gray-500" />;
    }
  };

  return (
    <div className="flex-1 bg-[#0a0a0f] p-8 overflow-y-auto">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-white mb-2">Team Settings</h1>
        <p className="text-sm text-muted-foreground">Manage your team workspace and members</p>
      </div>

      <div className="space-y-8 max-w-5xl">
        {/* Team Details */}
        <Card className="bg-[#121219]/80 border-white/10">
          <CardHeader>
            <CardTitle>Team Details</CardTitle>
            <CardDescription>Update your team's public profile</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex gap-4 items-end">
              <div className="flex-1 space-y-2">
                <label className="text-sm font-medium text-white">Team Name</label>
                <Input 
                  value={teamName}
                  onChange={(e) => setTeamName(e.target.value)}
                  className="bg-black/20 border-white/10 text-white"
                />
              </div>
              <Button onClick={handleUpdateName} disabled={loading || teamName === teamInfo?.name}>
                Save Changes
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Invite Members */}
        <Card className="bg-[#121219]/80 border-white/10">
          <CardHeader>
            <CardTitle>Invite Members</CardTitle>
            <CardDescription>Add new members to your team</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-2">
              <label className="text-sm font-medium text-white">Invite Link</label>
              <div className="flex gap-2">
                <div className="flex-1 bg-black/40 border border-white/10 rounded-md px-3 py-2 text-sm text-muted-foreground truncate">
                  {teamInfo?.invite_link || 'No invite link available'}
                </div>
                <Button variant="outline" onClick={handleCopyInvite}>
                  {copySuccess || 'Copy Link'}
                </Button>
                <Button variant="ghost" onClick={() => onRegenerateInvite?.()}>
                  <RefreshCw className="w-4 h-4" />
                </Button>
              </div>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium text-white">Send Email Invitations</label>
              <div className="space-y-2">
                <textarea
                  value={inviteEmails}
                  onChange={(e) => {
                    setInviteEmails(e.target.value);
                    if (inviteError) setInviteError('');
                  }}
                  placeholder="Enter email addresses separated by commas"
                  className={cn(
                    "w-full h-24 bg-black/20 border rounded-md p-3 text-sm text-white placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary",
                    inviteError ? "border-red-500/50" : "border-white/10"
                  )}
                />
                {inviteError && (
                  <div className="flex items-center gap-2 text-red-400 text-sm">
                    <AlertCircle className="w-4 h-4 flex-shrink-0" />
                    <span>{inviteError}</span>
                  </div>
                )}
                <Button onClick={handleSendInvites} disabled={!inviteEmails.trim() || loading || inviteLoading} className="w-full sm:w-auto">
                  <Send className="w-4 h-4 mr-2" /> {inviteLoading ? 'Sending...' : 'Send Invitations'}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Members List */}
        <Card className="bg-[#121219]/80 border-white/10">
          <CardHeader>
            <CardTitle>Team Members ({members?.length || 0})</CardTitle>
            <CardDescription>Manage member roles and access</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm text-left">
                <thead className="text-xs text-muted-foreground uppercase bg-white/5">
                  <tr>
                    <th className="px-4 py-3 rounded-l-lg">Member</th>
                    <th className="px-4 py-3">Role</th>
                    <th className="px-4 py-3">Joined</th>
                    <th className="px-4 py-3 rounded-r-lg text-right">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {(members || []).map((member) => (
                    <tr key={member.user_id} className="border-b border-white/5 last:border-0 hover:bg-white/5 transition-colors">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-primary to-purple-600 flex items-center justify-center text-xs font-bold text-white">
                            {getInitials(member.fullname)}
                          </div>
                          <div>
                            <div className="text-white font-medium">{member.fullname || 'Unknown'}</div>
                            <div className="text-xs text-muted-foreground">{member.email}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <Badge variant="outline" className={cn(
                          "border-opacity-30 capitalize",
                          member.role === 'admin' ? "bg-purple-500/10 text-purple-400 border-purple-500" : "bg-white/5 text-white/70 border-white/20"
                        )}>
                          {member.role}
                        </Badge>
                      </td>
                      <td className="px-4 py-3 text-muted-foreground">
                        {member.joined_at ? new Date(member.joined_at * 1000).toLocaleDateString() : '-'}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => handleRemoveMember(member)}
                          disabled={member.role === 'admin' || loading}
                          className="h-8 text-red-400 hover:text-red-300 hover:bg-red-500/10"
                        >
                          Remove
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        {/* Activity Log */}
        <Card className="bg-[#121219]/80 border-white/10">
          <CardHeader>
            <CardTitle>Activity Log</CardTitle>
            <CardDescription>Recent team events and changes</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {(activityLog || []).map((activity) => (
                <div key={activity.id} className="flex items-start gap-4 p-3 rounded-lg hover:bg-white/5 transition-colors">
                  <div className="mt-1 bg-white/10 p-2 rounded-full">
                    {getActivityIcon(activity.type)}
                  </div>
                  <div className="flex-1">
                    <p className="text-sm text-white">{activity.description}</p>
                    <p className="text-xs text-muted-foreground mt-1">
                      {new Date(activity.timestamp * 1000).toLocaleString()}
                    </p>
                  </div>
                </div>
              ))}
              
              {(!activityLog || activityLog.length === 0) && (
                <div className="text-center py-8 text-muted-foreground">No activity yet</div>
              )}

              {activityHasMore && (
                <Button 
                  variant="ghost" 
                  onClick={() => loadMoreActivities?.(teamInfo?.id)} 
                  disabled={loading}
                  className="w-full mt-4"
                >
                  Load More
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Confirmation Modal */}
      {confirmModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
          <div className="w-full max-w-md bg-[#1e1e1e] border border-white/10 rounded-xl shadow-2xl p-6">
            <h3 className="text-xl font-bold text-white mb-2">{confirmModal.title}</h3>
            <p className="text-muted-foreground mb-6">{confirmModal.message}</p>
            <div className="flex justify-end gap-3">
              <Button variant="outline" onClick={() => setConfirmModal(null)}>
                Cancel
              </Button>
              <Button 
                variant="destructive" 
                onClick={confirmModal.onConfirm}
                className="bg-red-500 hover:bg-red-600"
              >
                Confirm
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TeamSettings;
